import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel } from '@mui/material';
import { useAuthStore } from '../../store/authStore';
import { apiRequest } from '../../services/api';
import DeleteIcon from '@mui/icons-material/Delete';

interface User {
  id: number;
  username: string;
  email: string;
  phone_number: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

const UserManagementDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { role } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [newUser, setNewUser] = useState<Partial<User> & { password?: string }>({ role: 'viewer', is_active: true });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      const data = await apiRequest<User[]>('/users');
      setUsers(data);
    } catch (e) {
      setError('Failed to fetch users');
    }
  };

  useEffect(() => {
    if (open && role === 'admin') fetchUsers();
  }, [open, role]);

  const handleAddUser = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiRequest('/users', {
        method: 'POST',
        body: JSON.stringify(newUser),
      });
      setNewUser({ role: 'viewer', is_active: true });
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to add user');
    }
    setLoading(false);
  };

  const handleDeleteUser = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      await apiRequest(`/users/${id}`, { method: 'DELETE' });
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to delete user');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>User Management</DialogTitle>
      <DialogContent>
        {error && <Typography color="error">{error}</Typography>}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u: User) => (
              <TableRow key={u.id}>
                <TableCell>{u.username}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.phone_number}</TableCell>
                <TableCell>{u.role}</TableCell>
                <TableCell>{u.is_active ? 'Yes' : 'No'}</TableCell>
                <TableCell>{u.created_at}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleDeleteUser(u.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="h6" sx={{ mt: 2 }}>Add New User</Typography>
        <TextField label="Username" value={newUser.username || ''} onChange={e => setNewUser({ ...newUser, username: e.target.value })} fullWidth margin="dense" />
        <TextField label="Email" value={newUser.email || ''} onChange={e => setNewUser({ ...newUser, email: e.target.value })} fullWidth margin="dense" />
        <TextField label="Phone" value={newUser.phone_number || ''} onChange={e => setNewUser({ ...newUser, phone_number: e.target.value })} fullWidth margin="dense" />
        <TextField label="Password" type="password" value={newUser.password || ''} onChange={e => setNewUser({ ...newUser, password: e.target.value })} fullWidth margin="dense" />
        <FormControl fullWidth margin="dense">
          <InputLabel>Role</InputLabel>
          <Select value={newUser.role || 'viewer'} onChange={e => setNewUser({ ...newUser, role: e.target.value as string })}>
            <MenuItem value="admin">admin</MenuItem>
            <MenuItem value="operator">operator</MenuItem>
            <MenuItem value="viewer">viewer</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel control={<Switch checked={!!newUser.is_active} onChange={e => setNewUser({ ...newUser, is_active: e.target.checked })} />} label="Active" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleAddUser} disabled={loading}>Add User</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserManagementDialog; 